/* What a page costs, read off the service and remembered between visits.
 *
 * **Why anything is remembered at all.** The landing screen has to state what a
 * page costs before a parent spends one, and the request that answers takes a
 * moment. Without a remembered answer the cost sentence is absent on the first
 * paint of every launch, which is where it matters most. So the service is
 * asked, and what it answers is written down for the next launch to quote while
 * the next request is still in flight.
 *
 * **Per child, because the meter is per learner.** Two children on one phone
 * have two balances, and a single remembered entitlement would quote one
 * child's credits under the other's name.
 *
 * **What is not done here.** No threshold, no arithmetic on a balance, no
 * opinion about whether a page will be charged for. `credits.low` is the
 * service's judgment and `billable` is the service's answer. This file carries
 * them across and adds nothing. The pure readers are in `meter.ts`.
 */
import { useEffect, useSyncExternalStore } from "react";
import { entitlement as fetchEntitlement } from "./api";
import type { Entitlement } from "./types";

/** The key for one child, or for the device before there is a child. The
 *  no-child answer is what a learner minted in the next minute would hold,
 *  which is what the landing screen is quoting. */
function keyFor(childId?: string): string {
  return childId ? `mh.ent.${childId}` : "mh.ent.fresh";
}

const listeners = new Set<() => void>();

/** Memory first, storage behind it. The map is the snapshot source, so a
 *  component re-renders when a refresh lands rather than on every read. */
const seen = new Map<string, Entitlement | null>();

function fromStorage(key: string): Entitlement | null {
  try {
    const raw = localStorage.getItem(key);
    if (!raw) return null;
    const held = JSON.parse(raw) as Entitlement;
    /* The same shape check `api.ts` makes, and for the same reason: what
       proves a body is an entitlement is `entitled` and `pages_used`, never
       `pages_included`, whose meaning changed under the credits meter. */
    return typeof held?.entitled === "boolean"
      && typeof held?.pages_used === "number" ? held : null;
  } catch {
    // Storage throws rather than returning null in Safari private browsing,
    // and a cost sentence is never worth a blank screen.
    return null;
  }
}

/** What the service last told this device about this child, or null on a
 *  device it has never told. */
export function last(childId?: string): Entitlement | null {
  const key = keyFor(childId);
  if (!seen.has(key)) seen.set(key, fromStorage(key));
  return seen.get(key) ?? null;
}

/** Write down what the service just said. Called wherever an entitlement
 *  arrives, which is the launch ask, the dry run, the submit, the thread and
 *  every settled page.
 *
 *  Null writes nothing rather than clearing. A request that did not land is not
 *  an answer, and letting one wipe the record would take the cost sentence off
 *  the screen of every parent whose connection dropped. */
export function remember(ent: Entitlement | null | undefined,
                         childId?: string): void {
  if (!ent) return;
  const key = keyFor(childId);
  seen.set(key, ent);
  try {
    localStorage.setItem(key, JSON.stringify(ent));
  } catch {
    /* nothing to do, and nothing worth telling anyone about */
  }
  for (const fn of listeners) fn();
}

/** Drop it, because the learner it counted has been erased. The counters were
 *  about pages that no longer exist, and quoting them back on the next launch
 *  would be a number about work we have just deleted. */
export function forget(childId?: string): void {
  const key = keyFor(childId);
  seen.set(key, null);
  try {
    localStorage.removeItem(key);
  } catch {
    /* nothing to do about it */
  }
  for (const fn of listeners) fn();
}

/** Ask the service, and remember what it says.
 *
 *  It fails soft: `api.entitlement` answers null for anything that is not a
 *  200, and null leaves the remembered answer where it was. Nothing on this
 *  path may sit between a parent and the camera. */
export async function refresh(childId?: string): Promise<Entitlement | null> {
  const fresh = await fetchEntitlement(childId);
  if (fresh) remember(fresh, childId);
  return fresh;
}

function subscribe(fn: () => void): () => void {
  listeners.add(fn);
  return () => {
    listeners.delete(fn);
  };
}

/** The remembered meter for one child, refreshed once on mount.
 *
 *  Answers immediately with whatever is remembered, including null on a first
 *  launch, so a screen renders the sentence it can and never waits on a
 *  request to paint. */
export function useEntitlement(childId?: string): Entitlement | null {
  const held = useSyncExternalStore(
    subscribe,
    () => last(childId),
    () => last(childId),
  );
  useEffect(() => {
    void refresh(childId);
  }, [childId]);
  return held;
}
